// Phase 29.5.1 · Sales Return in-call tab UI.
//
// Rendered as the 4th tab inside RetailerDashboard (the in-call retailer
// surface). Reuses:
//   * offlineMutate  → the same PWA queue Secondary Order / Payments / Claims use.
//                      /api/sale-returns needs NO new registration; the mutate
//                      client is path-agnostic.
//   * /photos/upload → the same photo endpoint AttendanceScreen + VisitStart use.
//   * /admin/sale-return-reasons → live, active-only lookup source (Phase 29.5).
//
// Submits to POST /api/sale-returns with the Phase-29.5 payload shape:
//   { retailer_id, distributor_id?, visit_id?, gps, lines:[{sku_id, qty_kg,
//     reason_code, photo_ref?, note?}], idempotency_key, offline_captured, client_ts }
// Note: reason_code (not reason) — Phase 29.5 OpenAPI drift was fixed;
// respect the live schema.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PlusCircle, Trash2, Loader2, Camera, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { mutate as offlineMutate } from "@/offline/apiClient";
import AuthImage from "@/components/AuthImage";
import {
  validateLine as _validateLine,
  buildSaleReturnPayload as _buildSaleReturnPayload,
} from "@/pages/field/_phase29_5_1_helpers";

// Re-exported so downstream callers keep the same import path.
export const validateLine = _validateLine;
export const buildSaleReturnPayload = _buildSaleReturnPayload;

const NEW_LINE = () => ({
  sku_id: "",
  sku_code: "",
  sku_name: "",
  qty_kg: "",
  reason_code: "",
  photo_ref: null,
  note: "",
});

// Pure helper — validates one line. Kept here as `_localValidateLine` for
// local use; the exported symbol comes from _phase29_5_1_helpers above.
function _localValidateLine(line) {
  return _validateLine(line);
}

function _localBuildSaleReturnPayload(args) {
  return _buildSaleReturnPayload(args);
}

export default function SalesReturnPanel({ retailer_id, distributor_id, visit_id }) {
  const [lines, setLines] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [skus, setSkus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [gps, setGps] = useState(null);
  const [submitted, setSubmitted] = useState(null);
  const uploadRef = useRef({}); // index -> uploading bool

  // Load reasons (active only) + orderable SKUs list (reuse visibility from Secondary Order).
  const loadRefs = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: rr }, { data: ss }] = await Promise.all([
        api.get("/admin/sale-return-reasons"),
        api.get(`/retailers/${retailer_id}/orderable-skus`),
      ]);
      setReasons((rr.items || []).filter((r) => r.is_active !== false));
      setSkus(ss.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [retailer_id]);

  useEffect(() => { loadRefs(); }, [loadRefs]);

  // Live GPS (best-effort — same pattern as AttendanceScreen).
  useEffect(() => {
    if (!navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition(
      (p) => setGps({ lat: p.coords.latitude, lng: p.coords.longitude,
                       accuracy_m: p.coords.accuracy }),
      () => setGps(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  const addLine = () => setLines((prev) => [...prev, NEW_LINE()]);
  const removeLine = (idx) =>
    setLines((prev) => prev.filter((_, i) => i !== idx));
  const patchLine = (idx, patch) =>
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  const uploadPhoto = async (idx) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.capture = "environment";
    input.onchange = async () => {
      const f = input.files?.[0];
      if (!f) return;
      uploadRef.current[idx] = true;
      try {
        const form = new FormData();
        form.append("file", f, "return.jpg");
        form.append("context", "sale_return");
        const { data } = await api.post("/photos/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        patchLine(idx, { photo_ref: data.ref });
        toast.success("Photo attached");
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        uploadRef.current[idx] = false;
      }
    };
    input.click();
  };

  const perLineErrors = useMemo(() => lines.map(validateLine), [lines]);
  const canSubmit = lines.length > 0
    && perLineErrors.every((e) => e === null)
    && !!gps;

  const submit = async () => {
    if (!canSubmit) {
      if (!gps) toast.error("Waiting for GPS…");
      return;
    }
    setBusy(true);
    try {
      const idempotency_key = (crypto.randomUUID?.()
        || `sret-${Date.now()}-${Math.random().toString(36).slice(2)}`);
      const payload = buildSaleReturnPayload({
        retailer_id, distributor_id, visit_id, gps, lines, idempotency_key,
      });
      const r = await offlineMutate({
        method: "POST", path: "/sale-returns", body: payload,
        entity_type: "sale_returns",
      });
      if (!r.immediate) {
        toast.info("Queued offline — sale return submits once we're online");
        setSubmitted({ queued: true, idempotency_key });
        setLines([]);
        return;
      }
      setSubmitted(r.response);
      setLines([]);
      toast.success("Sale return submitted");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="py-8 text-center text-neutral-400">
        <Loader2 className="h-5 w-5 inline animate-spin" />
      </div>
    );
  }

  if (submitted) {
    return (
      <Card className="border-emerald-200 rounded-2xl bg-emerald-50/60"
            data-testid="sret-submitted">
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center gap-2 text-emerald-800 font-semibold">
            <CheckCircle2 className="h-5 w-5" />
            {submitted.queued ? "Queued for sync" : "Sale return submitted"}
          </div>
          <div className="text-xs text-neutral-600">
            {submitted.queued
              ? `Idempotency: ${submitted.idempotency_key}`
              : `Return #${(submitted.id || "").slice(0, 8)} · ${Number(submitted.total_qty_kg ?? 0)} kg across ${submitted.total_lines} line(s)`}
          </div>
          <Button size="sm" variant="outline"
                  data-testid="sret-new-return"
                  onClick={() => setSubmitted(null)}>
            Start another return <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="sret-panel">
      {lines.length === 0 ? (
        <Card className="border-dashed border-neutral-300 rounded-2xl">
          <CardContent className="p-6 text-center space-y-3">
            <div className="text-sm text-neutral-500">
              No return lines yet.
            </div>
            <Button size="sm"
                    onClick={addLine}
                    data-testid="sret-line-add"
                    className="tulsi-gradient text-white h-9 rounded-lg">
              <PlusCircle className="h-4 w-4 mr-1.5" />
              Add first return line
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {lines.map((line, idx) => (
            <LineRow
              key={idx}
              line={line}
              idx={idx}
              error={perLineErrors[idx]}
              skus={skus}
              reasons={reasons}
              uploading={!!uploadRef.current[idx]}
              onPatch={(patch) => patchLine(idx, patch)}
              onRemove={() => removeLine(idx)}
              onUpload={() => uploadPhoto(idx)}
            />
          ))}
          <Button
            size="sm"
            variant="outline"
            onClick={addLine}
            data-testid="sret-line-add"
            className="w-full h-9 rounded-lg"
          >
            <PlusCircle className="h-4 w-4 mr-1.5" />
            Add another line
          </Button>
        </>
      )}

      {lines.length > 0 && (
        <div className="pt-2">
          {!gps && (
            <div className="text-[11px] text-amber-700 mb-2 text-center"
                 data-testid="sret-gps-wait">
              Waiting for GPS…
            </div>
          )}
          <Button
            size="lg"
            onClick={submit}
            disabled={!canSubmit || busy}
            data-testid="sret-submit"
            className="w-full h-11 rounded-lg tulsi-gradient text-white"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Submit return ({lines.length} line{lines.length > 1 ? "s" : ""})
          </Button>
        </div>
      )}
    </div>
  );
}

function LineRow({ line, idx, error, skus, reasons, uploading,
                   onPatch, onRemove, onUpload }) {
  return (
    <Card className="border-neutral-200 rounded-2xl"
          data-testid="sret-line-row"
          data-line-index={idx}>
      <CardContent className="p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-mono text-neutral-400">
            Line #{idx + 1}
          </div>
          <button
            type="button"
            onClick={onRemove}
            data-testid="sret-remove-line"
            className="text-[11px] text-red-600 flex items-center gap-1"
          >
            <Trash2 className="h-3 w-3" /> Remove
          </button>
        </div>

        {/* SKU picker — same list Secondary Order draws from */}
        <div>
          <div className="text-[11px] text-neutral-500 mb-1">SKU</div>
          <select
            value={line.sku_id}
            data-testid="sret-sku-select"
            onChange={(e) => {
              const sku = skus.find((s) => s.sku_id === e.target.value);
              onPatch({
                sku_id: e.target.value,
                sku_code: sku?.sku_code || "",
                sku_name: sku?.name || "",
              });
            }}
            className="w-full h-10 rounded-lg border border-neutral-300 px-3 text-sm bg-white"
          >
            <option value="">— Pick a SKU —</option>
            {skus.map((s) => (
              <option key={s.sku_id} value={s.sku_id}>
                {s.sku_code} · {s.name}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="text-[11px] text-neutral-500 mb-1">Qty (kg)</div>
            <Input
              type="number"
              min="0.01"
              step="0.01"
              inputMode="decimal"
              value={line.qty_kg}
              onChange={(e) => onPatch({ qty_kg: e.target.value })}
              data-testid="sret-qty-input"
              className="h-10"
            />
          </div>
          <div>
            <div className="text-[11px] text-neutral-500 mb-1">Reason</div>
            <select
              value={line.reason_code}
              onChange={(e) => onPatch({ reason_code: e.target.value })}
              data-testid="sret-reason-select"
              className="w-full h-10 rounded-lg border border-neutral-300 px-3 text-sm bg-white"
            >
              <option value="">— Reason —</option>
              {reasons.map((r) => (
                <option key={r.code} value={r.code}>{r.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <div className="text-[11px] text-neutral-500 mb-1">
            Photo (optional)
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              type="button"
              variant="outline"
              onClick={onUpload}
              disabled={uploading}
              data-testid="sret-photo-upload"
              className="h-9 rounded-lg"
            >
              {uploading
                ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                : <Camera className="h-4 w-4 mr-1.5" />}
              {line.photo_ref ? "Replace" : "Attach"}
            </Button>
            {line.photo_ref && (
              <AuthImage
                refId={line.photo_ref} w={80}
                alt="return photo"
                className="h-9 w-9 rounded object-cover border border-neutral-200"
                data-testid="sret-photo-thumb"
              />
            )}
          </div>
        </div>

        <div>
          <div className="text-[11px] text-neutral-500 mb-1">Note (optional)</div>
          <Input
            value={line.note}
            onChange={(e) => onPatch({ note: e.target.value })}
            placeholder="e.g. wet cartons, expired batch, etc."
            maxLength={280}
            data-testid="sret-note-input"
            className="h-10"
          />
        </div>

        {error && (
          <div className="text-[11px] text-red-600 pt-1"
               data-testid="sret-line-error">
            {error}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
